// Stock turnover (rotación de inventario).
// Calcula días de cobertura por SKU, productos sin movimiento y rotación.
// Datos: productos.stock_actual + movimientos_stock (salidas).

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`

export default function StockTurnover() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [productos, setProductos] = useState([])
  const [movimientos, setMovimientos] = useState([])
  const [meses, setMeses] = useState(3)
  const [orden, setOrden] = useState('rotacion')   // rotacion | cobertura | sin_venta | valor

  useEffect(() => { cargar() }, [meses])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      const desde = new Date()
      desde.setMonth(desde.getMonth() - meses)
      desde.setDate(1)
      const desdeStr = desde.toISOString().slice(0, 10)

      const [{ data: prods, error: pe }, { data: mv, error: mve }] = await Promise.all([
        supabase.from('productos').select('id, sku, nombre, presentacion, categoria, stock_actual, stock_minimo, costo_unitario, activo'),
        supabase.from('movimientos_stock').select('producto_id, tipo, cantidad, created_at').gte('created_at', desdeStr),
      ])
      if (pe) throw pe
      if (mve) throw mve
      setProductos(prods || [])
      setMovimientos(mv || [])
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  // Necesitamos producto_id pero solo trajimos sku. Hago segunda consulta o asumimos formato.
  // Mejor: traer también id en productos
  // (lo arreglamos abajo)

  const filas = useMemo(() => {
    // Salidas por producto en el período
    const salidasPorProd = new Map()
    movimientos.forEach(m => {
      const tipo = (m.tipo || '').toLowerCase()
      if (['venta', 'salida', 'consumo', 'kit', 'pedido'].includes(tipo)) {
        salidasPorProd.set(m.producto_id, (salidasPorProd.get(m.producto_id) || 0) + Math.abs(Number(m.cantidad || 0)))
      }
    })

    const diasPeriodo = meses * 30
    return productos.map(p => {
      // No tenemos relación sku→id directa con esta query; pero si productos tiene id real,
      // hay que pedirlo. Por ahora salidas = 0 si no matchea.
      const salidas = salidasPorProd.get(p.id) || 0
      const consumoDiario = diasPeriodo > 0 ? salidas / diasPeriodo : 0
      const dias_cobertura = consumoDiario > 0 ? Math.round(p.stock_actual / consumoDiario) : null
      const rotacion_anual = p.stock_actual > 0 ? (salidas / p.stock_actual) * (365 / diasPeriodo) : 0
      const valor_inventario = Number(p.stock_actual || 0) * Number(p.costo_unitario || 0)
      return {
        sku: p.sku,
        nombre: p.nombre,
        presentacion: p.presentacion,
        categoria: p.categoria,
        stock_actual: p.stock_actual,
        costo_unitario: p.costo_unitario,
        valor_inventario,
        salidas,
        consumo_diario: consumoDiario,
        dias_cobertura,
        rotacion_anual,
        sin_venta: salidas === 0,
        activo: p.activo,
      }
    })
  }, [productos, movimientos, meses])

  const ordenadas = useMemo(() => {
    const arr = [...filas]
    if (orden === 'rotacion') arr.sort((a, b) => b.rotacion_anual - a.rotacion_anual)
    if (orden === 'cobertura') arr.sort((a, b) => (b.dias_cobertura ?? 99999) - (a.dias_cobertura ?? 99999))
    if (orden === 'sin_venta') arr.sort((a, b) => (b.sin_venta ? 1 : 0) - (a.sin_venta ? 1 : 0))
    if (orden === 'valor') arr.sort((a, b) => b.valor_inventario - a.valor_inventario)
    return arr
  }, [filas, orden])

  const totales = useMemo(() => {
    const t = { skus: filas.length, sin_venta: 0, valor_total: 0, valor_sin_venta: 0, salidas_total: 0 }
    filas.forEach(r => {
      t.valor_total += r.valor_inventario
      t.salidas_total += r.salidas
      if (r.sin_venta) {
        t.sin_venta += 1
        t.valor_sin_venta += r.valor_inventario
      }
    })
    return t
  }, [filas])

  return (
    <AdminLayout>
      <div className="page">
        <h1>📦 Stock turnover (rotación)</h1>
        <p className="muted">Días de cobertura, rotación anual y deadstock por SKU. Para optimizar capital de trabajo.</p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
            Período:
            <select value={meses} onChange={e => setMeses(Number(e.target.value))}
              style={{ marginLeft: 8, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
              <option value={1}>Último mes</option>
              <option value={3}>Últimos 3 meses</option>
              <option value={6}>Últimos 6 meses</option>
              <option value={12}>Último año</option>
            </select>
          </label>
          <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
            Ordenar por:
            <select value={orden} onChange={e => setOrden(e.target.value)}
              style={{ marginLeft: 8, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
              <option value="rotacion">Mayor rotación</option>
              <option value="cobertura">Más días stock</option>
              <option value="sin_venta">Sin ventas (deadstock)</option>
              <option value="valor">Más valor en inventario</option>
            </select>
          </label>
        </div>

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>SKUs activos</h3>
            <div className="stat">{totales.skus}</div>
          </div>
          <div className="card">
            <h3>Valor inventario</h3>
            <div className="stat" style={{ color: '#1a3461' }}>{fmtUYU(totales.valor_total)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>capital trabado</div>
          </div>
          <div className="card">
            <h3>Sin ventas (deadstock)</h3>
            <div className="stat" style={{ color: '#ef4444' }}>{totales.sin_venta}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{fmtUYU(totales.valor_sin_venta)} parado</div>
          </div>
          <div className="card">
            <h3>Unidades vendidas</h3>
            <div className="stat" style={{ color: '#10B981' }}>{totales.salidas_total}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>en {meses} mes(es)</div>
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && ordenadas.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="usu-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ textAlign: 'center' }}>Stock actual</th>
                  <th style={{ textAlign: 'center' }}>Salidas período</th>
                  <th style={{ textAlign: 'center' }}>Consumo/día</th>
                  <th style={{ textAlign: 'center' }}>Días cobertura</th>
                  <th style={{ textAlign: 'center' }}>Rotación anual</th>
                  <th style={{ textAlign: 'right' }}>Valor inventario</th>
                </tr>
              </thead>
              <tbody>
                {ordenadas.map(r => (
                  <tr key={r.sku}>
                    <td>
                      <div style={{ fontWeight: 700, color: '#1a3461' }}>{r.nombre}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {r.sku} {r.presentacion && `· ${r.presentacion}`} {r.categoria && `· ${r.categoria}`}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{r.stock_actual}</td>
                    <td style={{ textAlign: 'center', color: r.salidas > 0 ? '#10B981' : '#cbd5e1', fontWeight: 600 }}>
                      {r.salidas || '—'}
                    </td>
                    <td style={{ textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
                      {r.consumo_diario > 0 ? r.consumo_diario.toFixed(2) : '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {r.dias_cobertura == null
                        ? <span style={{ padding: '2px 8px', borderRadius: 999, background: '#fee2e2', color: '#991b1b', fontSize: 11, fontWeight: 700 }}>SIN VENTA</span>
                        : <span style={{
                            padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                            background: r.dias_cobertura > 90 ? '#fee2e2' : r.dias_cobertura > 30 ? '#fef3c7' : '#d1fae5',
                            color: r.dias_cobertura > 90 ? '#991b1b' : r.dias_cobertura > 30 ? '#92400e' : '#065f46',
                          }}>
                            {r.dias_cobertura}d
                          </span>}
                    </td>
                    <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                      {r.rotacion_anual > 0 ? `${r.rotacion_anual.toFixed(1)}x` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUYU(r.valor_inventario)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AnalisisIA
          reportType="stock_turnover"
          data={{ totales, productos: ordenadas.slice(0, 30), periodo_meses: meses }}
        />

        <div style={{ marginTop: 20, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f' }}>
          <strong>📌 Notas:</strong> Días cobertura = stock actual ÷ consumo diario promedio del período.
          Verde &lt;30d, amarillo 30-90d (mucho capital trabado), rojo &gt;90d o sin venta (deadstock).
          Rotación anual = cuántas veces se vacía el stock en un año (más alto = mejor).
        </div>
      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th { text-align: left; padding: 12px 14px; background: #f8fafc; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-transform: uppercase; color: #6b7280; font-weight: 700; letter-spacing: .03em; }
        .usu-table tbody td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
      `}</style>
    </AdminLayout>
  )
}
